import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api-client';
import { useI18n, type TranslationKey } from '@/lib/i18n';
import { getCached } from '@/lib/reference-data-cache';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, DropdownSelect, EmptyState, ErrorState, Input, PageHeader, SkeletonRow, Textarea, useConfirm } from '@/components/ui';

/**
 * Admin > SLA Policies: list and full-page editor (SLA-08).
 *
 * Replaces the generic name/description CRUD in SettingsPages.tsx with a real
 * per-priority target grid, business-hours/24/7 calculation, simple escalation,
 * and a condition builder (groups/companies/sources). Modelled on
 * the same list+editor chrome as RolesAdminPages/AgentsAdminPages.
 */

export type SlaPolicy = {
  id: string;
  name: string;
  description?: string | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  firstResponseMinutes: number;
  resolutionMinutes: number;
  escalationMinutes?: number | null;
  isActive: boolean;
  isDefault?: boolean;
  position: number;
  conditions?: SlaConditions | null;
  createdAt?: string;
};

type SlaConditions = {
  matchType?: 'all' | 'any';
  groupIds?: string[];
  companyIds?: string[];
  sources?: string[];
  ticketTypeIds?: string[];
};

type TicketType = { id: string; key: string; label: string };

type Group = { id: string; name: string };
type Company = { id: string; name: string };

type StatusDefinition = {
  id: string;
  key: string;
  label: string;
  legacyValue: string;
  isActive: boolean;
  isTerminal: boolean;
  pausesSla: boolean;
  sortOrder: number;
};

type PreviewMetric = {
  metric: 'first_response' | 'resolution';
  state: string;
  targetMinutes: number | null;
  dueAt: string | null;
  warningAt: string | null;
  escalationAt: string | null;
};

type PreviewResponse = {
  matchedPolicy: SlaPolicy | null;
  businessHoursSnapshot: { mode?: string; calendarName?: string | null; timezone?: string | null } | null;
  metrics: PreviewMetric[];
  evaluatedPolicies: Array<{
    id: string;
    position: number;
    isDefault: boolean;
    matched: boolean;
    selection?: string;
    conditionMatched?: boolean;
    conditions?: SlaConditions | null;
  }>;
  calculationTrace: unknown;
  missingConfiguration: string[];
};

const PRIORITIES: SlaPolicy['priority'][] = ['low', 'medium', 'high', 'urgent'];
const SOURCES = ['email', 'portal', 'phone', 'chat', 'web_form', 'api'];
const PREVIEW_METRIC_KEYS: Record<PreviewMetric['metric'], TranslationKey> = {
  first_response: 'sla.preview.metricFirstResponse',
  resolution: 'sla.preview.metricResolution',
};

type TFn = ReturnType<typeof useI18n>['t'];

const problem = (error: unknown, t: TFn) =>
  error instanceof ApiError ? error.message : t('common.requestFailed');

const fieldClass =
  'h-10 w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink shadow-card transition-all duration-150 hover:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1';

/** Display minutes in human-readable format: 30m, 1h, 4h 15m, etc. */
function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  if (!message) return null;
  return <ErrorState message={message} onRetry={onRetry} />;
}

function AdminHeader({
  breadcrumb,
  title,
  subtitle,
  actions,
}: {
  breadcrumb: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <PageHeader
      className="mb-0"
      breadcrumb={[{ label: t('shell.settings'), to: '/settings' }, { label: breadcrumb }]}
      title={title}
      description={subtitle}
      actions={actions}
    />
  );
}

/** Compact inline summary of non-empty condition arrays. */
function ConditionsSummary({ conditions }: { conditions?: SlaConditions | null }) {
  const { t } = useI18n();
  if (!conditions) return null;
  const parts: string[] = [];
  if (conditions.groupIds?.length) parts.push(t('sla.conditions.groups', { count: conditions.groupIds.length }));
  if (conditions.companyIds?.length) parts.push(t('sla.conditions.companies', { count: conditions.companyIds.length }));
  if (conditions.sources?.length) parts.push(t('sla.conditions.sources', { list: conditions.sources.join(', ') }));
  if (conditions.ticketTypeIds?.length) parts.push(t('sla.conditions.ticketTypes', { count: conditions.ticketTypeIds.length }));
  if (parts.length === 0) return null;
  const matchLabel = conditions.matchType === 'any' ? t('sla.conditions.anyOf') : t('sla.conditions.allOf');
  return (
    <p className="mt-0.5 text-xs text-ink-subtle">
      {t('sla.conditions.summary', { mode: matchLabel, parts: parts.join(' · ') })}
    </p>
  );
}

function formatPreviewDate(value: string | null | undefined, t: TFn, language: string, timezone?: string | null): string {
  if (!value) return t('sla.notConfigured');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t('sla.invalidServerValue');
  return date.toLocaleString(language, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

function StatusDefinitionsSection() {
  const { t } = useI18n();
  const [statuses, setStatuses] = React.useState<StatusDefinition[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      setStatuses(await api.get<StatusDefinition[]>('/admin/status-definitions'));
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function update(status: StatusDefinition, field: 'pausesSla' | 'isTerminal', checked: boolean) {
    const next = { ...status, [field]: checked };
    if (next.pausesSla && next.isTerminal) {
      setError(t('sla.statusSemantics.conflict'));
      return;
    }
    setSavingId(status.id);
    try {
      setError('');
      const updated = await api.put<StatusDefinition>(`/admin/status-definitions/${status.id}`, {
        // Send both explicit booleans on every change. The backend rejects
        // missing semantics rather than silently applying a default.
        pausesSla: next.pausesSla,
        isTerminal: next.isTerminal,
      });
      setStatuses((current) => current.map((item) => item.id === status.id ? updated : item));
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setSavingId(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('sla.statusSemantics.title')}</CardTitle>
        <p className="text-sm font-normal text-ink-muted">
          {t('sla.statusSemantics.description')}
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {error && <div className="px-6 pt-1"><ErrorState message={error} onRetry={() => void load()} /></div>}
        {loading ? <SkeletonRow rows={4} /> : statuses.length === 0 ? (
          <EmptyState title={t('sla.statusSemantics.emptyTitle')} description={t('sla.statusSemantics.emptyDescription')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-page text-left text-xs uppercase tracking-wide text-ink-subtle">
                <tr>
                  <th className="px-6 py-3 font-medium">{t('sla.status')}</th>
                  <th className="px-4 py-3 font-medium">{t('sla.active')}</th>
                  <th className="px-4 py-3 font-medium">{t('sla.statusSemantics.colPauses')}</th>
                  <th className="px-6 py-3 font-medium">{t('sla.statusSemantics.colStops')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {statuses.map((status) => (
                  <tr key={status.id}>
                    <td className="px-6 py-3">
                      <div className="font-medium text-ink">{status.label}</div>
                      <div className="text-xs text-ink-subtle">{status.key} · {status.legacyValue}</div>
                    </td>
                    <td className="px-4 py-3"><Badge variant={status.isActive ? 'success' : 'neutral'}>{status.isActive ? t('sla.active') : t('sla.inactive')}</Badge></td>
                    <td className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 text-ink-muted">
                        <input
                          type="checkbox"
                          aria-label={t('sla.statusSemantics.pausesAria', { label: status.label })}
                          checked={status.pausesSla}
                          disabled={savingId === status.id || !status.isActive}
                          onChange={(event) => void update(status, 'pausesSla', event.target.checked)}
                          className="h-4 w-4 rounded border-line-strong text-brand-600 focus:ring-2 focus:ring-brand-500"
                        />
                        {status.pausesSla ? t('sla.yes') : t('sla.no')}
                      </label>
                    </td>
                    <td className="px-6 py-3">
                      <label className="inline-flex items-center gap-2 text-ink-muted">
                        <input
                          type="checkbox"
                          aria-label={t('sla.statusSemantics.stopsAria', { label: status.label })}
                          checked={status.isTerminal}
                          disabled={savingId === status.id || !status.isActive}
                          onChange={(event) => void update(status, 'isTerminal', event.target.checked)}
                          className="h-4 w-4 rounded border-line-strong text-brand-600 focus:ring-2 focus:ring-brand-500"
                        />
                        {status.isTerminal ? t('sla.yes') : t('sla.no')}
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SlaPolicyPreview({
  groups,
  companies,
  ticketTypes,
  priority,
}: {
  groups: Group[];
  companies: Company[];
  ticketTypes: TicketType[];
  priority: SlaPolicy['priority'];
}) {
  const { t, tOr, language } = useI18n();
  const [previewPriority, setPreviewPriority] = React.useState(priority);
  const [groupId, setGroupId] = React.useState('');
  const [companyId, setCompanyId] = React.useState('');
  const [source, setSource] = React.useState('');
  const [ticketTypeId, setTicketTypeId] = React.useState('');
  const [startAt, setStartAt] = React.useState(() => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const [result, setResult] = React.useState<PreviewResponse | null>(null);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  async function preview() {
    setLoading(true);
    try {
      setError('');
      const response = await api.post<PreviewResponse>('/admin/sla-policies/preview', {
        priority: previewPriority,
        ...(groupId ? { groupId } : {}),
        ...(companyId ? { companyId } : {}),
        ...(source ? { source } : {}),
        ...(ticketTypeId ? { ticketTypeId } : {}),
        ...(startAt ? { startAt: new Date(startAt).toISOString() } : {}),
      });
      setResult(response);
    } catch (cause) {
      setResult(null);
      setError(problem(cause, t));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('sla.preview.title')}</CardTitle>
        <p className="text-sm font-normal text-ink-muted">
          {t('sla.preview.description')}
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <DropdownSelect
            label={t('sla.preview.priority')}
            options={PRIORITIES.map((value) => ({ value, label: tOr(`priority.${value}`, value) }))}
            value={previewPriority}
            onChange={(value) => setPreviewPriority(value as SlaPolicy['priority'])}
            searchable={false}
          />
          <DropdownSelect
            label={t('sla.preview.group')}
            options={[{ value: '', label: t('sla.preview.anyGroup') }, ...groups.map((item) => ({ value: item.id, label: item.name }))]}
            value={groupId}
            onChange={setGroupId}
          />
          <DropdownSelect
            label={t('sla.preview.company')}
            options={[{ value: '', label: t('sla.preview.anyCompany') }, ...companies.map((item) => ({ value: item.id, label: item.name }))]}
            value={companyId}
            onChange={setCompanyId}
          />
          <DropdownSelect
            label={t('sla.preview.source')}
            options={[{ value: '', label: t('sla.preview.anySource') }, ...SOURCES.map((value) => ({ value, label: tOr(`source.${value}`, value) }))]}
            value={source}
            onChange={setSource}
            searchable={false}
          />
          <DropdownSelect
            label={t('sla.preview.ticketType')}
            options={[{ value: '', label: t('sla.preview.anyTicketType') }, ...ticketTypes.map((item) => ({ value: item.id, label: item.label }))]}
            value={ticketTypeId}
            onChange={setTicketTypeId}
          />
          <Input
            label={t('sla.preview.representativeStart')}
            type="datetime-local"
            value={startAt}
            onChange={(event) => setStartAt(event.target.value)}
          />
          <div className="md:col-span-2 xl:col-span-3">
            <Button type="button" disabled={loading || !startAt} onClick={() => void preview()}>{loading ? t('sla.preview.calculating') : t('sla.preview.run')}</Button>
          </div>
        </div>

        {error && <ErrorState message={error} />}
        {result && (
          <div className="space-y-4 border-t border-line pt-5">
            {result.missingConfiguration.length > 0 && (
              <div role="alert" className="rounded-lg border border-warning-500/30 bg-warning-50 px-4 py-3 text-sm text-warning-700">
                <p className="font-semibold">{t('sla.preview.configAttention')}</p>
                <ul className="mt-1 list-disc pl-5">{result.missingConfiguration.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-surface-page p-3"><p className="text-xs uppercase tracking-wide text-ink-subtle">{t('sla.preview.selectedPolicy')}</p><p className="mt-1 font-semibold text-ink">{result.matchedPolicy?.name ?? t('sla.preview.noPolicy')}</p></div>
              <div className="rounded-lg bg-surface-page p-3"><p className="text-xs uppercase tracking-wide text-ink-subtle">{t('sla.preview.evaluationOrder')}</p><p className="mt-1 font-semibold text-ink">{t('sla.preview.firstMatching')}</p></div>
              <div className="rounded-lg bg-surface-page p-3"><p className="text-xs uppercase tracking-wide text-ink-subtle">{t('sla.preview.calculationMode')}</p><p className="mt-1 font-semibold text-ink">{result.businessHoursSnapshot?.mode === 'business_hours' ? (result.businessHoursSnapshot.calendarName ? t('sla.preview.modeBusinessHoursNamed', { name: result.businessHoursSnapshot.calendarName }) : t('sla.preview.modeBusinessHours')) : result.businessHoursSnapshot?.mode === '24x7' ? t('sla.preview.mode247') : t('sla.preview.modeNone')}</p></div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-ink">{t('sla.preview.thresholds')}</h3>
              <div className="overflow-x-auto rounded-lg border border-line">
                <table className="w-full text-sm">
                  <thead className="bg-surface-page text-left text-xs uppercase tracking-wide text-ink-subtle"><tr><th className="px-3 py-2">{t('sla.preview.colMetric')}</th><th className="px-3 py-2">{t('sla.preview.colTarget')}</th><th className="px-3 py-2">{t('sla.preview.colWarning')}</th><th className="px-3 py-2">{t('sla.preview.colDue')}</th><th className="px-3 py-2">{t('sla.preview.colEscalation')}</th><th className="px-3 py-2">{t('sla.preview.colState')}</th></tr></thead>
                  <tbody className="divide-y divide-line">
                    {result.metrics.map((metric) => <tr key={metric.metric}>
                      <td className="px-3 py-2 font-medium text-ink">{t(PREVIEW_METRIC_KEYS[metric.metric])}</td>
                      <td className="px-3 py-2 text-ink-muted">{metric.targetMinutes == null ? t('sla.notConfigured') : t('sla.minutesValue', { value: formatMinutes(metric.targetMinutes) })}</td>
                      <td className="px-3 py-2 text-ink-muted">{formatPreviewDate(metric.warningAt, t, language)}</td>
                      <td className="px-3 py-2 text-ink-muted">{formatPreviewDate(metric.dueAt, t, language)}</td>
                      <td className="px-3 py-2 text-ink-muted">{formatPreviewDate(metric.escalationAt, t, language)}</td>
                      <td className="px-3 py-2"><Badge variant={metric.state === 'not_configured' ? 'neutral' : 'info'}>{tOr(`sla.state.${metric.state}`, metric.state.replace(/_/g, ' '))}</Badge></td>
                    </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-ink">{t('sla.preview.trace')}</h3>
              <ol className="space-y-2 text-sm">
                {result.evaluatedPolicies.map((candidate) => <li key={candidate.id} className="flex flex-wrap items-center gap-2 rounded-md border border-line px-3 py-2">
                  <span className="font-mono text-xs text-ink-subtle">#{candidate.position + 1}</span>
                  <span className="font-medium text-ink">{candidate.id}</span>
                  <Badge variant={candidate.matched ? 'success' : 'neutral'}>{candidate.matched ? t('sla.preview.selected') : candidate.conditionMatched ? t('sla.preview.matchedAfterSelection') : t('sla.preview.skipped')}</Badge>
                  {candidate.isDefault && <Badge variant="info">{t('sla.default')}</Badge>}
                  {candidate.selection && <span className="text-xs text-ink-subtle">{candidate.selection}</span>}
                </li>)}
              </ol>
            </div>
            <details className="rounded-md border border-line px-3 py-2">
              <summary className="cursor-pointer text-sm font-medium text-ink">{t('sla.preview.showTrace')}</summary>
              <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-ink px-3 py-2 text-xs text-white">{JSON.stringify(result.calculationTrace, null, 2)}</pre>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── List ─────────────────────────────────────────────────────────────────────

export function SlaPoliciesListPage() {
  const { t, tOr } = useI18n();
  const { confirm } = useConfirm();
  const [policies, setPolicies] = React.useState<SlaPolicy[]>([]);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [reordering, setReordering] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      setPolicies(await api.get<SlaPolicy[]>('/admin/sla-policies'));
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function remove(policy: SlaPolicy) {
    if (!await confirm({ title: t('sla.list.deleteTitle', { name: policy.name }), description: t('sla.list.deleteDescription'), confirmLabel: t('sla.list.deleteConfirm') })) return;
    try {
      await api.delete(`/admin/sla-policies/${policy.id}`);
      await load();
    } catch (cause) {
      setError(problem(cause, t));
    }
  }

  async function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= policies.length) return;
    const reordered = [...policies];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setPolicies(reordered);
    setReordering(true);
    try {
      await api.put<SlaPolicy[]>('/admin/sla-policies/reorder', {
        orderedIds: reordered.map((policy) => policy.id),
      });
    } catch (cause) {
      setError(problem(cause, t));
      await load();
    } finally {
      setReordering(false);
    }
  }

  async function duplicate(policy: SlaPolicy) {
    try {
      setError('');
      await api.post(`/admin/sla-policies/${policy.id}/duplicate`);
      await load();
    } catch (cause) {
      setError(problem(cause, t));
    }
  }

  async function toggleActive(policy: SlaPolicy) {
    try {
      setError('');
      await api.put(`/admin/sla-policies/${policy.id}/status`, { isActive: !policy.isActive });
      await load();
    } catch (cause) {
      setError(problem(cause, t));
    }
  }

  async function setDefault(policy: SlaPolicy) {
    try {
      setError('');
      await api.put(`/admin/sla-policies/${policy.id}/default`);
      await load();
    } catch (cause) {
      setError(problem(cause, t));
    }
  }

  return (
    <div className="animate-fade-in space-y-6">
      <AdminHeader
        breadcrumb={t('nav.slaPolicies')}
        title={t('nav.slaPolicies')}
        subtitle={t('sla.list.subtitle')}
        actions={
          <Link
            to="/settings/sla-policies/new"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white shadow-card transition-all duration-150 hover:bg-brand-700 hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {t('sla.list.newPolicy')}
          </Link>
        }
      />
      <ErrorBanner message={error} onRetry={() => void load()} />

      <Card className="overflow-hidden">
        <CardContent className="divide-y divide-line p-0">
          {loading && <SkeletonRow rows={4} />}
          {!loading && policies.length === 0 && (
            <EmptyState
              title={t('sla.list.emptyTitle')}
              description={t('sla.list.emptyDescription')}
            />
          )}
          {policies.map((policy, index) => (
            <div
              key={policy.id}
              className="group flex flex-wrap items-center justify-between gap-4 p-5 transition-colors duration-150 hover:bg-brand-50/50"
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    aria-label={t('sla.list.moveUp')}
                    disabled={reordering || index === 0}
                    onClick={() => void move(index, -1)}
                    className="rounded text-ink-subtle hover:text-brand-600 disabled:opacity-30"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    aria-label={t('sla.list.moveDown')}
                    disabled={reordering || index === policies.length - 1}
                    onClick={() => void move(index, 1)}
                    className="rounded text-ink-subtle hover:text-brand-600 disabled:opacity-30"
                  >
                    ▼
                  </button>
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/settings/sla-policies/${policy.id}`}
                      className="font-semibold text-brand-700 transition-colors hover:text-brand-800 hover:underline"
                    >
                      {policy.name}
                    </Link>
                    <Badge>{tOr(`priority.${policy.priority}`, policy.priority)}</Badge>
                    {policy.isDefault && (
                      <Badge className="border-info-600/20 bg-info-50 text-info-600">{t('sla.default')}</Badge>
                    )}
                    {!policy.isActive && <Badge variant="neutral">{t('sla.inactive')}</Badge>}
                  </div>
                  {policy.description && (
                    <p className="mt-1 max-w-xl text-sm text-ink-muted">{policy.description}</p>
                  )}
                  <ConditionsSummary conditions={policy.conditions} />
                </div>
              </div>

              <div className="flex items-center gap-6 text-sm text-ink-muted">
                <span>{t('sla.list.frShort', { value: formatMinutes(policy.firstResponseMinutes) })}</span>
                <span>{t('sla.list.resolveShort', { value: formatMinutes(policy.resolutionMinutes) })}</span>
                {policy.escalationMinutes != null && <span>{t('sla.list.escalateShort', { value: formatMinutes(policy.escalationMinutes) })}</span>}
                <div className="flex gap-2 opacity-80 transition-opacity duration-150 group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => void toggleActive(policy)}
                    className={`inline-flex h-8 items-center justify-center rounded-lg border px-3 text-xs font-medium shadow-card transition-all duration-150 ${
                      policy.isActive
                        ? 'border-warning-500/40 bg-warning-50 text-warning-700 hover:bg-warning-50'
                        : 'border-success-500/40 bg-success-50 text-success-700 hover:bg-success-50'
                    }`}
                  >
                    {policy.isActive ? t('sla.list.deactivate') : t('sla.list.activate')}
                  </button>
                  {!policy.isDefault && (
                    <button
                      type="button"
                      onClick={() => void setDefault(policy)}
                      className="inline-flex h-8 items-center justify-center rounded-lg border border-info-600/40 bg-info-50 px-3 text-xs font-medium text-info-600 shadow-card transition-all duration-150 hover:bg-info-50"
                    >
                      {t('sla.list.setDefault')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void duplicate(policy)}
                    className="inline-flex h-8 items-center justify-center rounded-lg border border-line-strong bg-surface px-3 text-xs font-medium text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                  >
                    {t('sla.list.duplicate')}
                  </button>
                  <Link
                    to={`/settings/sla-policies/${policy.id}`}
                    className="inline-flex h-8 items-center justify-center rounded-lg border border-line-strong bg-surface px-3 text-xs font-medium text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                  >
                    {t('sla.edit')}
                  </Link>
                  <Button size="sm" variant="destructive" onClick={() => void remove(policy)}>
                    {t('common.delete')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <StatusDefinitionsSection />
    </div>
  );
}

// ─── Editor ───────────────────────────────────────────────────────────────────

export function SlaPolicyEditPage() {
  const { t, tOr } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [groups, setGroups] = React.useState<Group[]>([]);
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [ticketTypes, setTicketTypes] = React.useState<TicketType[]>([]);

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [priority, setPriority] = React.useState<SlaPolicy['priority']>('medium');
  const [firstResponseMinutes, setFirstResponseMinutes] = React.useState(4);
  const [resolutionMinutes, setResolutionMinutes] = React.useState(24);
  const [escalationMinutes, setEscalationMinutes] = React.useState<number | ''>('');
  const [clearEscalation, setClearEscalation] = React.useState(false);
  const [position, setPosition] = React.useState(0);
  const [isActive, setActive] = React.useState(true);
  const [isDefault, setIsDefault] = React.useState(false);
  const [matchType, setMatchType] = React.useState<'all' | 'any'>('all');
  const [conditions, setConditions] = React.useState<SlaConditions>({});

  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        setError('');
        const [groupList, companyList, ticketTypeList] = await Promise.all([
          getCached<Group[]>('/admin/groups'),
          getCached<Company[]>('/companies'),
          api.get<TicketType[]>('/admin/ticket-types').catch(() => [] as TicketType[]),
        ]);
        if (cancelled) return;
        setGroups(groupList);
        setCompanies(companyList);
        setTicketTypes(ticketTypeList);

        if (!isNew) {
          const existing = await api.get<SlaPolicy>(`/admin/sla-policies/${id}`);
          if (cancelled) return;
          setName(existing.name);
          setDescription(existing.description ?? '');
          setPriority(existing.priority);
          setFirstResponseMinutes(existing.firstResponseMinutes);
          setResolutionMinutes(existing.resolutionMinutes);
          setEscalationMinutes(existing.escalationMinutes ?? '');
          setClearEscalation(false);
          setPosition(existing.position);
          setActive(existing.isActive);
          setIsDefault(existing.isDefault ?? false);
          setMatchType(existing.conditions?.matchType ?? 'all');
          setConditions(existing.conditions ?? {});
        }
      } catch (cause) {
        if (!cancelled) setError(problem(cause, t));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, t]);

  function toggleCondition(key: keyof SlaConditions, value: string, checked: boolean) {
    setSaved(false);
    setConditions((current) => {
      const list = new Set((current[key] as string[] | undefined) ?? []);
      if (checked) list.add(value);
      else list.delete(value);
      return { ...current, [key]: Array.from(list) };
    });
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      setError('');
      setSaved(false);
      // Drop empty arrays so an untouched condition group means "applies to
      // all", matching how the backend's conditionsMatch() treats an absent key.
      const cleanedConditions: Record<string, unknown> = Object.fromEntries(
        Object.entries(conditions).filter(
          ([key, value]) => key !== 'matchType' && Array.isArray(value) && value.length > 0,
        ),
      );
      cleanedConditions.matchType = matchType;
      const payload = {
        name,
        description: description || undefined,
        priority,
        firstResponseMinutes: Number(firstResponseMinutes),
        resolutionMinutes: Number(resolutionMinutes),
        escalationMinutes: clearEscalation ? null : escalationMinutes === '' ? undefined : Number(escalationMinutes),
        isActive,
        isDefault,
        position: Number(position),
        conditions: Object.keys(cleanedConditions).length > 1 ? cleanedConditions : undefined,
      };
      if (isNew) {
        const created = await api.post<SlaPolicy>('/admin/sla-policies', payload);
        navigate(`/settings/sla-policies/${created.id}`, { replace: true });
      } else {
        await api.put<SlaPolicy>(`/admin/sla-policies/${id}`, payload);
      }
      setSaved(true);
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="animate-fade-in space-y-6" onSubmit={save}>
      <AdminHeader
        breadcrumb={t('nav.slaPolicies')}
        title={isNew ? t('sla.edit.newTitle') : t('sla.edit.editTitle')}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate('/settings/sla-policies')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving || loading}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      />
      <ErrorBanner message={error} />
      {saved && (
        <div role="status" className="animate-fade-in rounded-xl border border-success-500/20 bg-success-50 px-4 py-3 text-sm text-success-700">
          {t('sla.edit.saved')}
        </div>
      )}

      <Card>
        <CardContent className="space-y-5 p-6">
          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
            <label htmlFor="sla-name" className="text-sm font-semibold text-ink">
              {t('sla.edit.name')} <span className="text-accent-500">*</span>
            </label>
            <Input id="sla-name" required value={name} onChange={(event) => setName(event.target.value)} />
          </div>

          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)]">
            <label htmlFor="sla-description" className="pt-2 text-sm font-semibold text-ink">{t('sla.description')}</label>
            <Textarea
              id="sla-description"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
            <label htmlFor="sla-priority" className="text-sm font-semibold text-ink">{t('sla.edit.appliesToPriority')}</label>
            <DropdownSelect
              options={PRIORITIES.map((value) => ({ value, label: tOr(`priority.${value}`, value) }))}
              value={priority}
              onChange={(value) => setPriority(value as SlaPolicy['priority'])}
              searchable={false}
            />
          </div>

          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
            <label htmlFor="sla-position" className="text-sm font-semibold text-ink">{t('sla.edit.evaluationOrder')}</label>
            <Input
              id="sla-position"
              type="number"
              min={0}
              step={1}
              value={position}
              onChange={(event) => { setPosition(Number(event.target.value)); setSaved(false); }}
              hint={t('sla.edit.evaluationOrderHint')}
            />
          </div>

          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
            <span className="text-sm font-semibold text-ink">{t('sla.status')}</span>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                checked={isActive}
                onChange={(event) => setActive(event.target.checked)}
              />
              {t('sla.active')}
            </label>
          </div>

          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
            <span className="text-sm font-semibold text-ink">{t('sla.edit.defaultPolicy')}</span>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                checked={isDefault}
                onChange={(event) => setIsDefault(event.target.checked)}
              />
              {t('sla.edit.useAsDefault')}
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-5 p-6">
          <h2 className="border-b border-line pb-3 text-lg font-semibold text-ink">{t('sla.edit.targets')}</h2>
          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-page text-left text-xs uppercase tracking-wide text-ink-subtle"><tr><th className="px-3 py-2">{t('sla.preview.colMetric')}</th><th className="px-3 py-2">{t('sla.preview.colTarget')}</th><th className="px-3 py-2">{t('sla.preview.colWarning')}</th><th className="px-3 py-2">{t('sla.preview.colEscalation')}</th></tr></thead>
              <tbody className="divide-y divide-line">
                <tr><td className="px-3 py-2 font-medium text-ink">{t('sla.edit.firstResponse')}</td><td className="px-3 py-2 text-ink-muted">{t('sla.minutesValue', { value: formatMinutes(Number(firstResponseMinutes)) })}</td><td className="px-3 py-2 text-ink-muted">{t('sla.edit.autoAt75')}</td><td className="px-3 py-2 text-ink-subtle">{t('sla.notConfigured')}</td></tr>
                <tr><td className="px-3 py-2 font-medium text-ink">{t('sla.edit.resolution')}</td><td className="px-3 py-2 text-ink-muted">{t('sla.minutesValue', { value: formatMinutes(Number(resolutionMinutes)) })}</td><td className="px-3 py-2 text-ink-muted">{t('sla.edit.autoAt75')}</td><td className="px-3 py-2 text-ink-muted">{escalationMinutes === '' || clearEscalation ? t('sla.notConfigured') : t('sla.edit.enabledAfter', { value: formatMinutes(Number(escalationMinutes)) })}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="inline-flex items-start gap-2 text-sm text-ink-muted">
              <input type="checkbox" checked={clearEscalation} onChange={(event) => setClearEscalation(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-line-strong text-brand-600 focus:ring-2 focus:ring-brand-500" />
              <span><span className="font-medium text-ink">{t('sla.edit.clearEscalation')}</span><span className="block text-xs text-ink-subtle">{t('sla.edit.clearEscalationHint')}</span></span>
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-muted">
                {t('sla.edit.firstResponseMinutes')} <span className="text-accent-500">*</span>
              </span>
              <input
                type="number"
                min={0}
                step="1"
                required
                className={fieldClass}
                value={firstResponseMinutes}
                onChange={(event) => setFirstResponseMinutes(Number(event.target.value))}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-muted">
                {t('sla.edit.resolutionMinutes')} <span className="text-accent-500">*</span>
              </span>
              <input
                type="number"
                min={0}
                step="1"
                required
                className={fieldClass}
                value={resolutionMinutes}
                onChange={(event) => setResolutionMinutes(Number(event.target.value))}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-muted">
                {t('sla.edit.escalateAfter')}
              </span>
              <input
                type="number"
                min={0}
                step="1"
                placeholder={t('sla.edit.notSet')}
                className={fieldClass}
                value={escalationMinutes}
                onChange={(event) => setEscalationMinutes(event.target.value === '' ? '' : Number(event.target.value))}
              />
            </label>
          </div>
          <p className="text-xs text-ink-subtle">
            {t('sla.edit.remindersNote')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-5 p-6">
          <div className="flex items-baseline justify-between border-b border-line pb-3">
            <h2 className="text-lg font-semibold text-ink">{t('sla.edit.conditions')}</h2>
            <span className="text-xs text-ink-subtle">{t('sla.edit.conditionsHint')}</span>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-ink">{t('sla.edit.matchMode')}</span>
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-ink">
              <input
                type="radio"
                name="matchType"
                className="h-4 w-4 border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                checked={matchType === 'all'}
                onChange={() => setMatchType('all')}
              />
              {t('sla.edit.matchAll')}
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-ink">
              <input
                type="radio"
                name="matchType"
                className="h-4 w-4 border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                checked={matchType === 'any'}
                onChange={() => setMatchType('any')}
              />
              {t('sla.edit.matchAny')}
            </label>
          </div>

          <ConditionGroup
            title={t('sla.edit.groups')}
            options={groups.map((group) => ({ value: group.id, label: group.name }))}
            selected={conditions.groupIds ?? []}
            onToggle={(value, checked) => toggleCondition('groupIds', value, checked)}
          />
          <ConditionGroup
            title={t('sla.edit.companies')}
            options={companies.map((company) => ({ value: company.id, label: company.name }))}
            selected={conditions.companyIds ?? []}
            onToggle={(value, checked) => toggleCondition('companyIds', value, checked)}
          />
          <ConditionGroup
            title={t('sla.edit.sources')}
            options={SOURCES.map((source) => ({ value: source, label: tOr(`source.${source}`, source) }))}
            selected={conditions.sources ?? []}
            onToggle={(value, checked) => toggleCondition('sources', value, checked)}
          />
          <ConditionGroup
            title={t('sla.edit.ticketTypes')}
            options={ticketTypes.map((tt) => ({ value: tt.id, label: tt.label }))}
            selected={(conditions.ticketTypeIds as string[] | undefined) ?? []}
            onToggle={(value, checked) => toggleCondition('ticketTypeIds', value, checked)}
          />
        </CardContent>
      </Card>

      <SlaPolicyPreview
        groups={groups}
        companies={companies}
        ticketTypes={ticketTypes}
        priority={priority}
      />
    </form>
  );
}

function ConditionGroup({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div className="border-t border-line pt-4 first:border-t-0 first:pt-0">
      <h3 className="mb-2 text-sm font-semibold text-ink">{title}</h3>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (
          <label key={option.value} className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
              checked={selected.includes(option.value)}
              onChange={(event) => onToggle(option.value, event.target.checked)}
            />
            {option.label}
          </label>
        ))}
      </div>
    </div>
  );
}
